// Contact.jsx
import React, { useState } from 'react';
import './Conatct.css'; // Corrected import
import { db } from '../../Firebase/firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup'; // Added Yup for validation
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaFacebookF, FaTwitter, FaLinkedinIn, FaPaperPlane, FaCheck } from 'react-icons/fa';

const Contact = () => {
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false); // New state to track submission

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('contact.validation.required')),
    email: Yup.string()
      .email(t('contact.validation.invalidEmail'))
      .required(t('contact.validation.required')),
    subject: Yup.string().required(t('contact.validation.required')),
    message: Yup.string()
      .min(10, t('contact.validation.shortMessage'))
      .required(t('contact.validation.required')),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await validationSchema.validate(formData);
    } catch (validationError) {
      setFormStatus({ type: 'error', message: validationError.message });
      return;
    }

    const dataToSubmit = {
      ...formData,
      timestamp: serverTimestamp(),
    };

    try {
      setIsLoading(true);
      await addDoc(collection(db, 'contactMessages'), dataToSubmit);
      setIsSubmitted(true); // Mark as submitted
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMessage = t('contact.errorMessage');

      if (error.code === 'permission-denied') {
        errorMessage = t('contact.permissionDenied');
      } else if (error.code === 'unavailable') {
        errorMessage = t('contact.serviceUnavailable');
      }

      setFormStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const isRTL = i18n.dir ? i18n.dir() === 'rtl' : false;
  const fieldProps = (field) => ({
    id: field,
    name: field,
    value: formData[field],
    onChange: handleChange,
    className: 'form-control',
    placeholder: t(`contact.placeholders.${field}`),
    required: true,
  });

  return (
    <section className="contact-section" dir={isRTL ? 'rtl' : undefined}>
      <div className="container">
        <div className="contact-head">
          <span className="ccf-eyebrow">{t('contact.formHeader')}</span>
          <h2 className="contact-title">
            {isSubmitted ? t('contact.thankYouTitle') : t('contact.getInTouch')}
          </h2>
          <div className="heading-line"></div>
        </div>

        <div className="contact-card">
          {/* Info panel */}
          <aside className="contact-info">
            <h3>{t('contact.formHeader')}</h3>
            <ul className="contact-info__list">
              <li>
                <span className="contact-info__icon"><FaPhoneAlt /></span>
                <div>
                  <small>{t('contact.labels.phone', 'Phone')}</small>
                  <a href="tel:+201000714875" dir="ltr">010 00714875</a>
                </div>
              </li>
              <li>
                <span className="contact-info__icon"><FaEnvelope /></span>
                <div>
                  <small>{t('contact.labels.email')}</small>
                  <a href="mailto:f.centroculturale@gmail.com">f.centroculturale@gmail.com</a>
                </div>
              </li>
              <li>
                <span className="contact-info__icon"><FaMapMarkerAlt /></span>
                <div>
                  <small>{t('contact.mapHeader')}</small>
                  <address>كنيسة ودير سان جوزيف - ٢ ش بنك مصر - متفرع من محمد فريد - وسط البلد, Cairo, Egypt</address>
                  <a
                    className="contact-info__directions"
                    href="https://www.google.com/maps/search/?api=1&query=2+Mohammed+Farid+Abdeen+Cairo"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('getDirections', 'Get directions')} →
                  </a>
                </div>
              </li>
            </ul>
            <div className="contact-info__social">
              <a href="https://www.facebook.com/profile.php?id=100083012838300" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebookF /></a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><FaTwitter /></a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedinIn /></a>
            </div>
          </aside>

          {/* Form panel */}
          <div className="contact-form-container">
            {isSubmitted ? (
              <div className="contact-thanks">
                <span className="contact-thanks__icon"><FaCheck /></span>
                <p className="thank-you-message">
                  {t('contact.thankYouMessage', { name: formData.name })}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="contact-row">
                  <div className="contact-field">
                    <label htmlFor="name" className="form-label">{t('contact.labels.name')}</label>
                    <input type="text" {...fieldProps('name')} />
                  </div>
                  <div className="contact-field">
                    <label htmlFor="email" className="form-label">{t('contact.labels.email')}</label>
                    <input type="email" {...fieldProps('email')} />
                  </div>
                </div>
                <div className="contact-field">
                  <label htmlFor="subject" className="form-label">{t('contact.labels.subject')}</label>
                  <input type="text" {...fieldProps('subject')} />
                </div>
                <div className="contact-field">
                  <label htmlFor="message" className="form-label">{t('contact.labels.message')}</label>
                  <textarea rows={5} {...fieldProps('message')}></textarea>
                </div>

                <button type="submit" className="btn btn-primary contact-submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {t('contact.sending')}
                    </>
                  ) : (
                    <>{t('contact.sendMessageButton')} <FaPaperPlane className="contact-submit__icon" /></>
                  )}
                </button>
                {formStatus.message && (
                  <p className={`form-status text-${formStatus.type}`} role="alert">{formStatus.message}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
